import {
  ChakraBox,
  ChakraFlex,
  ChakraText,
  ChakraToolTip,
  CircularProgress,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Info,
  RechartLine,
  RechartLineChart,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { formatHeight, round } from '~/lib/util/clinicalProfile.util';

import { ObservationCards } from './constants';

const { neutral } = EquipUIFireflyV2Theme.colors;
const { h4, label: labelStyle, paragraph } = EquipUITheme.typography;

export type GraphData = { value: number };

type ClinicalMeasurementCardProps = {
  graphData?: GraphData[];
  id: string;
  isLoading?: boolean;
  mainObservation: ObservationContent;
  onClick?: () => void;
  profileType: ObservationCards;
  secondaryText?: string;
  title?: string;
  tooltipContent?: string;
};

export type ObservationContent = {
  observationDatetime?: string;
  secondaryValue?: number;
  unit?: string;
  value: number;
};

const getDisplayValue = (
  shouldFormatBloodPressure: boolean,
  shouldFormatHeight: boolean,
  secondaryValue: number,
  value: number,
): string => {
  if (shouldFormatBloodPressure && (value || secondaryValue)) {
    return `${value ?? 0}/${secondaryValue ?? 0}`;
  }

  if (value) {
    if (shouldFormatHeight) return formatHeight(value);
    return `${round(value)}`;
  }
  return null;
};

const ClinicalMeasurementCard = ({
  graphData,
  id,
  isLoading,
  mainObservation,
  onClick,
  profileType,
  secondaryText,
  title,
  tooltipContent,
}: ClinicalMeasurementCardProps) => {
  const { observationDatetime, unit, secondaryValue, value } =
    mainObservation ?? {};
  const shouldFormatHeight =
    profileType === ObservationCards.HEIGHT && unit?.startsWith('in');
  const shouldFormatBloodPressure =
    profileType === ObservationCards.BLOOD_PRESSURE;

  const displayValue = getDisplayValue(
    shouldFormatBloodPressure,
    shouldFormatHeight,
    secondaryValue,
    value,
  );

  const shouldHideUnitSpace =
    profileType === ObservationCards.BMI || shouldFormatHeight;
  let displayUnit = shouldFormatHeight ? '' : unit;
  if (!shouldHideUnitSpace) displayUnit = ` ${displayUnit}`;

  return (
    <ChakraBox
      border={`1px solid ${neutral.line}`}
      borderRadius="16px"
      cursor="pointer"
      minWidth="100px"
      onClick={onClick}
      overflow="hidden"
      padding="16px"
      position="relative"
    >
      <ChakraFlex alignItems="center" marginBottom="7px">
        <ChakraText {...labelStyle} color="secondary.50" id={`${id}__title`}>
          {title}
        </ChakraText>
        {profileType === ObservationCards.TARGET_WEIGHT && (
          <ChakraBox id={`${id}__tooltip`} marginLeft="8px">
            <ChakraToolTip
              bg="white"
              border={`1px solid ${neutral.line}`}
              borderRadius="8px"
              color="neutral.secondary"
              hasArrow
              label={tooltipContent}
              marginLeft="8px"
              maxWidth="320px"
              padding="8px"
              shouldWrapChildren
            >
              <Info height="16px" width="16px" />
            </ChakraToolTip>
          </ChakraBox>
        )}
      </ChakraFlex>
      <ChakraFlex justifyContent="space-between">
        <ChakraBox>
          <ChakraText
            {...h4}
            color="neutral.primary"
            id={`${id}__primary-text`}
            whiteSpace="nowrap"
          >
            {value || (shouldFormatBloodPressure && displayValue)
              ? `${displayValue}${displayUnit ?? ''}`
              : 'NA'}
          </ChakraText>
          {secondaryText && (
            <ChakraText
              {...paragraph}
              id={`${id}__secondary-text`}
              lineHeight="16.8px"
            >
              {secondaryText}
            </ChakraText>
          )}
          <ChakraText
            {...labelStyle}
            color="neutral.secondary"
            id={`${id}__time`}
            marginTop="6px"
            overflow="visible"
            width="max-content"
          >
            {observationDatetime
              ? DateTime.fromISO(observationDatetime)
                  .toFormat('MM/dd/yyyy h:mma')
                  .toLowerCase()
              : 'NA'}
          </ChakraText>
        </ChakraBox>
        <ChakraBox marginTop="8px">
          {(graphData ?? []).length > 1 && (
            <RechartLineChart data={graphData} height={40} width={80}>
              <defs>
                <linearGradient
                  id="strokeColor"
                  x1="0%"
                  x2="100%"
                  y1="0%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#E35050" />
                  <stop offset="100%" stopColor="#0E497A" />
                </linearGradient>
              </defs>
              <RechartLine
                dataKey="value"
                dot={false}
                stroke="url(#strokeColor)"
                strokeWidth="2px"
              />
            </RechartLineChart>
          )}
        </ChakraBox>
      </ChakraFlex>
      {isLoading && (
        <ChakraFlex
          alignItems="center"
          background="white"
          bottom={0}
          justifyContent="center"
          left={0}
          position="absolute"
          right={0}
          top={0}
        >
          <CircularProgress id={id} isIndeterminate size="30px" />
        </ChakraFlex>
      )}
    </ChakraBox>
  );
};

export default ClinicalMeasurementCard;
